import React, { useState, useEffect, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Box, Grid, Typography, Pagination, Stack } from "@mui/material";
import PersonAddIcon from "@mui/icons-material/PersonAdd";

import PageContainer from "../layouts/PageContainer";
import PageToolbar from "../components/common/PageToolbar";
import AppButton from "../components/buttons/AppButton";
import ConfirmDialog from "../components/common/ConfirmDialog";
import CustomerCard from "../components/customers/CustomerCard";
import CreateCustomerModal from "../components/customers/CreateCustomerModal";
import UpdateCustomerModal from "../components/customers/UpdateCustomerModal";
import AppCircularProgress from "../components/common/AppCircularProgress";

import useDebounce from "../hooks/useDebounce";
import { usePermissions } from "../hooks/usePermissions";
import {
  getCustomers,
  getCustomerById,
  deleteCustomer,
  resetCustomerServerStatus,
} from "../redux/actions/customerActions";
import PageError from "../components/common/PageError";

const CustomersPage = () => {
  const dispatch = useDispatch();
  const { hasPermission } = usePermissions();

  const {
    customers,
    pagination,
    loadingCustomers,
    errorLoadingCustomers,
    serverStatus,
    loadingCustomerDelete,
    loadingCustomerUpdate,
    loadingCustomerCreate,
    errorCreatingCustomer,
    errorUpdatingCustomer,
    customer,
    loadingCustomer,
  } = useSelector((state) => state.customer);

  const [page, setPage] = useState(1);
  const [limit] = useState(12);
  const [searchTerm, setSearchTerm] = useState("");
  const debouncedSearch = useDebounce(searchTerm, 500);

  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isUpdateModalOpen, setIsUpdateModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [selectedId, setSelectedId] = useState(null);

  const canCreate = hasPermission("customer:create");

  const fetchCustomers = useCallback(() => {
    dispatch(getCustomers({ page, size: limit, search: debouncedSearch }));
  }, [dispatch, page, limit, debouncedSearch]);

  useEffect(() => {
    fetchCustomers();
  }, [fetchCustomers]);

  useEffect(() => {
    if (!serverStatus) return;
    const { operation, status } = serverStatus;
    if (operation === "create" && (status === 201 || status === 200))
      setIsCreateModalOpen(false);
    if (operation === "update" && status === 200) setIsUpdateModalOpen(false);
    if (operation === "delete" && status === 200) setIsDeleteModalOpen(false);
    dispatch(resetCustomerServerStatus());
  }, [serverStatus, dispatch]);

  const renderContent = () => {
    if (loadingCustomers) return <AppCircularProgress />;
    if (errorLoadingCustomers)
      return (
        <PageError error={errorLoadingCustomers} onClick={fetchCustomers} />
      );
    if (!customers || customers.length === 0)
      return (
        <Box textAlign="center" mt={5}>
          <Typography>No customers found.</Typography>
        </Box>
      );

    return (
      <>
        <Grid container spacing={3}>
          {customers.map((c) => (
            <Grid item key={c.id} xs={12} sm={6} md={4} lg={3}>
              <CustomerCard
                customer={c}
                onEdit={() => {
                  dispatch(getCustomerById(c.id));
                  setIsUpdateModalOpen(true);
                }}
                onDelete={() => {
                  setSelectedId(c.id);
                  setIsDeleteModalOpen(true);
                }}
              />
            </Grid>
          ))}
        </Grid>
        {pagination?.totalPages > 1 && (
          <Stack alignItems="center" mt={4}>
            <Pagination
              count={pagination.totalPages}
              page={page}
              onChange={(e, v) => setPage(v)}
              color="primary"
            />
          </Stack>
        )}
      </>
    );
  };

  return (
    <PageContainer
      title="Customer Directory"
      subtitle="Manage business accounts, credit limits, and loyalty programs."
      actions={
        canCreate && (
          <AppButton
            variant="contained"
            startIcon={<PersonAddIcon />}
            onClick={() => setIsCreateModalOpen(true)}
          >
            Register Customer
          </AppButton>
        )
      }
    >
      <PageToolbar
        searchTerm={searchTerm}
        onSearchChange={(e) => setSearchTerm(e.target.value)}
        searchPlaceholder="Search by name, contact or email..."
      />
      {renderContent()}

      {isCreateModalOpen && (
        <CreateCustomerModal
          open={isCreateModalOpen}
          onClose={() => setIsCreateModalOpen(false)}
          loading={loadingCustomerCreate}
          error={errorCreatingCustomer}
        />
      )}
      {isUpdateModalOpen && (
        <UpdateCustomerModal
          open={isUpdateModalOpen}
          onClose={() => setIsUpdateModalOpen(false)}
          customer={customer}
          loading={loadingCustomerUpdate || loadingCustomer}
          error={errorUpdatingCustomer}
        />
      )}
      {isDeleteModalOpen && (
        <ConfirmDialog
          open={isDeleteModalOpen}
          onClose={() => setIsDeleteModalOpen(false)}
          onConfirm={() => dispatch(deleteCustomer(selectedId))}
          title="Delete Customer"
          message="Permanently remove this customer? Historical sales records will be preserved but the profile will be deleted."
          loading={loadingCustomerDelete}
        />
      )}
    </PageContainer>
  );
};

export default CustomersPage;
